/**
 * Resolves the FE rollout gate for the interview-buffer code in the
 * Details step. Self-schedule modes ride `feInterviewBuffer`;
 * manual/adhoc ride `feManualInterviewBuffer`.
 *
 * The non-obvious case: a self-schedule interview that the candidate
 * confirmed becomes a booked `AtsInterview`, and re-opening it uses the
 * "Edit interview" action — which mounts the wizard in `mode="manual"`.
 * Its buffer should still follow the SELF-SCHEDULE flag, not the manual
 * one (same `ats_schedule_id` origin signal that drives "Send additional
 * slots"). `originSelfSchedule` carries that signal from the mount site
 * (the edit GET `/ats_interviews/:id` does not emit `ats_schedule_id`).
 *
 * Additive semantics: such an edit shows the buffer when EITHER flag is
 * on, so no case that shows the buffer today regresses.
 */
import type { ScheduleMode } from '../../../config/types';
import {
  isManualMode,
  isSelfScheduleMode,
} from '../../../config/modeClassifiers';

interface ResolveBufferFlagOnArgs {
  mode: ScheduleMode;
  apiReference?: string;
  feInterviewBuffer: boolean;
  feManualInterviewBuffer: boolean;
  /** Editing a booked interview that originated from a self-schedule
   *  (`ats_schedule_id` set on the source interview). */
  originSelfSchedule?: boolean;
}

export function resolveBufferFlagOn({
  mode,
  apiReference,
  feInterviewBuffer,
  feManualInterviewBuffer,
  originSelfSchedule,
}: ResolveBufferFlagOnArgs): boolean {
  const isSelfScheduleOriginEdit =
    apiReference === 'edit' && Boolean(originSelfSchedule);
  return (
    (feInterviewBuffer &&
      (isSelfScheduleMode(mode) || isSelfScheduleOriginEdit)) ||
    (feManualInterviewBuffer && isManualMode(mode))
  );
}
